const ui = (msgid) => {
  const text = String(msgid ?? '');
  if (!text) return '';
  const bag = typeof window !== 'undefined' ? window.hskiI18n?.msg : null;
  const value = bag && typeof bag[text] === 'string' ? bag[text] : '';
  return value || text;
};

/**
 * Fallback catalog mirroring SchoolCoursePackages (PHP).
 * Spec: Specyfikacja/Seeder/Oferta szkoleniowa/oferta-szkoleniowa.md §3
 * Prefer runtime data from `[data-course-packages]` on the booking root.
 */
export const COURSE_PACKAGES_CHILDREN = [
  {
    id: 'podstawowy',
    label: 'Pakiet podstawowy',
    note: '20 godz. szkolenia + 4 godz. atrakcji',
    price: 1350,
    badge: 'Najczęściej wybierany',
    meal_options: [],
  },
  {
    id: 'ekonomiczny',
    label: 'Pakiet ekonomiczny',
    note: '20 godz. szkolenia + 4 godz. atrakcji',
    price: 950,
    meal_options: [],
  },
];

export const COURSE_PACKAGES_ADULTS = [
  {
    id: 'podstawowy',
    label: 'Pakiet podstawowy',
    note: '20 godz. szkolenia + 4 godz. atrakcji',
    price: 1350,
    badge: 'Najczęściej wybierany',
    meal_options: [],
  },
  {
    id: 'ekonomiczny',
    label: 'Pakiet ekonomiczny',
    note: '20 godz. szkolenia + 4 godz. atrakcji',
    price: 950,
    meal_options: [],
  },
];

const daysFromMealLabel = (label) => {
  const match = String(label || '').match(/\((\d+)\s*dni\)/i)
    || String(label || '').match(/\b(\d+)\s*dni\b/i);
  return match ? Math.max(0, Number(match[1]) || 0) : 0;
};

export const normalizeMealOptions = (raw) => {
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  return raw
    .map((row) => {
      const id = String(row?.id || row?.plan_key || '');
      const label = String(row?.label || '');
      let pricePerDay = Math.max(0, Number(row?.price_per_day ?? row?.daily_rate) || 0);
      let days = Math.max(0, Number(row?.days ?? row?.meal_days) || 0);
      let price = Math.max(0, Number(row?.price) || 0);
      if (pricePerDay > 0 && days > 0) {
        price = pricePerDay * days;
      } else if (price > 0) {
        if (days <= 0) days = daysFromMealLabel(label);
        if (days > 0 && pricePerDay <= 0 && price % days === 0) {
          pricePerDay = price / days;
        }
      }
      return {
        id,
        label: ui(label || id),
        price,
        price_per_day: pricePerDay,
        days,
      };
    })
    .filter((row) => {
      if (!row.id || seen.has(row.id)) return false;
      seen.add(row.id);
      return true;
    });
};

const mapPackageRow = (pkg) => {
  const row = {
    id: String(pkg.id || ''),
    label: ui(String(pkg.label || pkg.select_label || pkg.name || '')),
    note: ui(String(pkg.note || pkg.desc || '')),
    price: Number(pkg.price_value ?? pkg.price) || 0,
    meal_ui: String(pkg.meal_ui || '') === 'multiple' ? 'multiple' : 'single',
    meal_options: normalizeMealOptions(pkg.meal_options),
  };
  if (pkg.badge) {
    row.badge = ui(String(pkg.badge));
  } else if (pkg.variant === 'featured') {
    row.badge = ui('Najczęściej wybierany');
  }
  return row;
};

export const parseCoursePackagesAttr = (raw) => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || !parsed.length) return null;
    return parsed
      .map(mapPackageRow)
      .filter((pkg) => pkg.id && pkg.label);
  } catch {
    return null;
  }
};

export const packagesFromSchoolCatalog = (root) => {
  const raw = root?.dataset?.schoolCourseCatalog || '';
  if (!raw) return null;
  let catalog;
  try {
    catalog = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!Array.isArray(catalog) || !catalog.length) return null;

  const params = new URLSearchParams(window.location.search);
  const location = params.get('location') || root?.dataset?.location || '';
  const courseId = params.get('course') || root?.dataset?.course || '';
  const dateFrom = params.get('date_from') || root?.dataset?.dateFrom || '';
  const dateTo = params.get('date_to') || root?.dataset?.dateTo || '';

  const slope = catalog.find((row) => row.id === location || row.destination === location) || catalog[0];
  const courses = Array.isArray(slope?.courses) ? slope.courses : [];
  const course = courses.find((row) => (
    (dateFrom && dateTo && row.date_from === dateFrom && row.date_to === dateTo)
    || row.id === courseId
    || row.value === courseId
  )) || courses[0];
  const packages = Array.isArray(course?.packages) ? course.packages : [];
  const mapped = packages
    .map(mapPackageRow)
    .filter((pkg) => pkg.id && pkg.label);

  return mapped.length ? mapped : null;
};

export const resolveCoursePackages = (root) => {
  const fromDom = parseCoursePackagesAttr(root?.dataset?.coursePackages);
  const fromCatalog = packagesFromSchoolCatalog(root);

  if (fromDom?.length) {
    // Dopnij meal_options z katalogu stok→kurs (SSR czasem ma starszy picker bez wyżywienia).
    if (fromCatalog?.length) {
      const byId = new Map(fromCatalog.map((pkg) => [pkg.id, pkg]));
      return fromDom.map((pkg) => {
        const cat = byId.get(pkg.id);
        if (!cat) return pkg;
        const meals = Array.isArray(cat.meal_options) && cat.meal_options.length
          ? cat.meal_options
          : pkg.meal_options;
        return {
          ...pkg,
          price: pkg.price || cat.price,
          meal_ui: cat.meal_ui || pkg.meal_ui || 'single',
          meal_options: meals || [],
        };
      });
    }
    return fromDom;
  }

  if (fromCatalog?.length) return fromCatalog;

  const course = new URLSearchParams(window.location.search).get('course')
    || root?.dataset?.course
    || '';
  if (course.includes('dorosl') || course.includes('adult')) {
    return COURSE_PACKAGES_ADULTS;
  }
  return COURSE_PACKAGES_CHILDREN;
};

export const findCoursePackage = (packages, packageId) => {
  if (!Array.isArray(packages) || !packageId) return null;
  return packages.find((pkg) => pkg.id === packageId) || null;
};
